import { Type } from 'class-transformer';
import {
  IsBooleanString,
  IsIn,
  IsInt,
  IsISO8601,
  IsOptional,
  IsString,
  Matches,
  Max,
  MaxLength,
  Min,
} from 'class-validator';
import {
  CONSULTANT_DEADLINE_BUCKETS,
  CONSULTANT_DEADLINE_DOMAINS,
  CONSULTANT_DEADLINES_LIST_MAX_PAGE_SIZE,
  CONSULTANT_DEADLINES_SUMMARY_MAX_PAGE_SIZE,
  type ConsultantDeadlineBucket,
  type ConsultantDeadlineDomain,
} from './consultant-deadline.dto';
import { ISO_DAY_PATTERN } from './deadline.constants';
import {
  DEADLINE_RECURRENCE_FILTERS,
  type DeadlineRecurrenceFilter,
} from './deadline-management.constants';

const ID_MAX_LENGTH = 64;
const SEARCH_MAX_LENGTH = 120;

export class ConsultantDeadlineSummaryQueryDto {
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(CONSULTANT_DEADLINES_SUMMARY_MAX_PAGE_SIZE)
  pageSize?: number;

  /** Match case-insensitive sul nome / ragione sociale del cliente. */
  @IsOptional()
  @IsString()
  @MaxLength(SEARCH_MAX_LENGTH)
  search?: string;

  @IsOptional()
  @IsString()
  @MaxLength(ID_MAX_LENGTH)
  tenantId?: string;

  /** Filtri di riga: i contatori contano solo le righe corrispondenti e i clienti senza corrispondenze sono esclusi. */
  @IsOptional()
  @IsIn(CONSULTANT_DEADLINE_BUCKETS)
  bucket?: ConsultantDeadlineBucket;

  @IsOptional()
  @IsIn(CONSULTANT_DEADLINE_DOMAINS)
  domain?: ConsultantDeadlineDomain;

  @IsOptional()
  @IsString()
  @MaxLength(ID_MAX_LENGTH)
  documentTypeId?: string;

  /** Testo libero su titolo e nome del tipo documento (case-insensitive). */
  @IsOptional()
  @IsString()
  @MaxLength(SEARCH_MAX_LENGTH)
  document?: string;

  @IsOptional()
  @Matches(ISO_DAY_PATTERN)
  @IsISO8601({ strict: true })
  dueFrom?: string;

  @IsOptional()
  @Matches(ISO_DAY_PATTERN)
  @IsISO8601({ strict: true })
  dueTo?: string;

  /** Spec §11.3: `RECURRING` = validità valorizzata, `ONE_OFF` = scadenza unica. */
  @IsOptional()
  @IsIn(DEADLINE_RECURRENCE_FILTERS)
  recurrence?: DeadlineRecurrenceFilter;

  /** `"true"`: solo righe archiviate (chiuse), con motivo; altrimenti solo attive. */
  @IsOptional()
  @IsBooleanString()
  archived?: string;
}

export class ListConsultantDeadlinesQueryDto {
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(CONSULTANT_DEADLINES_LIST_MAX_PAGE_SIZE)
  pageSize?: number;

  @IsOptional()
  @IsString()
  @MaxLength(ID_MAX_LENGTH)
  tenantId?: string;

  @IsOptional()
  @IsIn(CONSULTANT_DEADLINE_DOMAINS)
  domain?: ConsultantDeadlineDomain;

  @IsOptional()
  @IsString()
  @MaxLength(ID_MAX_LENGTH)
  documentTypeId?: string;

  /** Testo libero su titolo e nome del tipo documento (case-insensitive). */
  @IsOptional()
  @IsString()
  @MaxLength(SEARCH_MAX_LENGTH)
  document?: string;

  @IsOptional()
  @IsIn(CONSULTANT_DEADLINE_BUCKETS)
  bucket?: ConsultantDeadlineBucket;

  @IsOptional()
  @IsString()
  @MaxLength(ID_MAX_LENGTH)
  assigneeUserId?: string;

  @IsOptional()
  @Matches(ISO_DAY_PATTERN)
  @IsISO8601({ strict: true })
  dueFrom?: string;

  @IsOptional()
  @Matches(ISO_DAY_PATTERN)
  @IsISO8601({ strict: true })
  dueTo?: string;

  @IsOptional()
  @IsIn(DEADLINE_RECURRENCE_FILTERS)
  recurrence?: DeadlineRecurrenceFilter;

  /** `"true"`: solo righe archiviate (chiuse), con motivo; altrimenti solo attive. */
  @IsOptional()
  @IsBooleanString()
  archived?: string;
}
